import { Link, NavLink } from 'react-router-dom';
import { Menu, X, Mail, Phone, MapPin } from 'lucide-react';
import { SiFacebook, SiInstagram, SiLinkedin } from 'react-icons/si';
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ROUTE_PATHS, COMPANY_INFO } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { springPresets } from '@/lib/motion';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { to: ROUTE_PATHS.HOME, label: 'Accueil' },
    { to: ROUTE_PATHS.SERVICES, label: 'Services' },
    { to: ROUTE_PATHS.PORTFOLIO, label: 'Portfolio' },
    { to: ROUTE_PATHS.CONTACT, label: 'Contact' },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background/95 backdrop-blur-md shadow-lg border-b border-border'
            : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3 group">
              <motion.img
                src={IMAGES.WHATSAPP_IMAGE_2026_03_03_AT_00_57_58_37}
                alt="YKE Multi-Service Logo"
                className="h-14 w-auto"
                whileHover={{ scale: 1.05 }}
                transition={springPresets.snappy}
              />
              <div className="hidden sm:block">
                <h1 className="text-xl font-heading font-bold text-primary">
                  {COMPANY_INFO.name}
                </h1>
                <p className="text-xs text-muted-foreground italic">
                  {COMPANY_INFO.tagline}
                </p>
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-all duration-200 relative group ${
                      isActive ? 'text-primary' : 'text-foreground hover:text-primary'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {item.label}
                      <span
                        className={`absolute -bottom-1 left-0 h-0.5 bg-primary transition-all duration-300 ${
                          isActive ? 'w-full' : 'w-0 group-hover:w-full'
                        }`}
                      />
                    </>
                  )}
                </NavLink>
              ))}
            </nav>

            <Link
              to={ROUTE_PATHS.CONTACT}
              className="hidden lg:inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-full font-medium hover:scale-105 active:scale-95 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Contactez-nous
            </Link>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden border-t border-border bg-background/98 backdrop-blur-md"
            >
              <nav className="container mx-auto px-4 py-6 flex flex-col gap-4">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted text-foreground'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
                <Link
                  to={ROUTE_PATHS.CONTACT}
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 bg-primary text-primary-foreground rounded-lg font-medium text-center hover:scale-105 active:scale-95 transition-all duration-200"
                >
                  Contactez-nous
                </Link>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main style={{ paddingTop: `${headerHeight}px` }} className="flex-1">
        {children}
      </main>

      <footer className="bg-card border-t border-border mt-24">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img
                  src={IMAGES.WHATSAPP_IMAGE_2026_03_03_AT_00_57_58_37}
                  alt="YKE Multi-Service Logo"
                  className="h-12 w-auto"
                />
                <div>
                  <h3 className="text-lg font-heading font-bold text-primary">
                    {COMPANY_INFO.name}
                  </h3>
                  <p className="text-xs text-muted-foreground italic">
                    {COMPANY_INFO.tagline}
                  </p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {COMPANY_INFO.description}
              </p>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-4 text-foreground">Navigation</h4>
              <ul className="space-y-3">
                {navItems.map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-4 text-foreground">Services</h4>
              <ul className="space-y-3">
                <li className="text-sm text-muted-foreground">Design Résidentiel</li>
                <li className="text-sm text-muted-foreground">Design Commercial</li>
                <li className="text-sm text-muted-foreground">Consultation</li>
                <li className="text-sm text-muted-foreground">Ameublement Sur Mesure</li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-4 text-foreground">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-sm text-muted-foreground">
                  <MapPin size={16} className="mt-0.5 flex-shrink-0 text-primary" />
                  <span>{COMPANY_INFO.location}</span>
                </li>
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Phone size={16} className="flex-shrink-0 text-primary" />
                  <a
                    href={`tel:${COMPANY_INFO.phone}`}
                    className="hover:text-primary transition-colors"
                  >
                    {COMPANY_INFO.phone}
                  </a>
                </li>
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Mail size={16} className="flex-shrink-0 text-primary" />
                  <a
                    href={`mailto:${COMPANY_INFO.email}`}
                    className="hover:text-primary transition-colors break-all"
                  >
                    {COMPANY_INFO.email}
                  </a>
                </li>
              </ul>

              <div className="flex items-center gap-4 mt-6">
                <a
                  href="#"
                  className="p-2 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-all duration-200"
                  aria-label="Facebook"
                >
                  <SiFacebook size={18} />
                </a>
                <a
                  href="#"
                  className="p-2 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-all duration-200"
                  aria-label="Instagram"
                >
                  <SiInstagram size={18} />
                </a>
                <a
                  href="#"
                  className="p-2 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-all duration-200"
                  aria-label="LinkedIn"
                >
                  <SiLinkedin size={18} />
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-border mt-12 pt-8 text-center">
            <p className="text-sm text-muted-foreground">
              © 2026 {COMPANY_INFO.name}. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}